import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PatientPicker } from "@/components/opd/PatientPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarDays, Loader2, User, X } from "lucide-react";
import { toast } from "sonner";

function genSlots() {
  const out = [];
  [["09:00", "13:00"], ["14:00", "17:00"]].forEach(([s, e]) => {
    let [h, m] = s.split(":").map(Number);
    const [eh, em] = e.split(":").map(Number);
    while (h < eh || (h === eh && m < em)) {
      const t = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      m += 20; if (m >= 60) { m -= 60; h++; }
      out.push([t, `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`]);
    }
  });
  return out;
}
const SLOTS = genSlots();

export default function Appointments() {
  const { activeId, has } = useFacility();
  const [meta, setMeta] = useState({ doctors: [] });
  const [doctorId, setDoctorId] = useState("");
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [slot, setSlot] = useState(null);
  const [patient, setPatient] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (activeId) api.get(`/opd/meta?facility_id=${activeId}`).then((r) => { setMeta(r.data); if (r.data.doctors[0]) setDoctorId(r.data.doctors[0].id); }).catch(() => {}); }, [activeId]);

  const load = useCallback(() => {
    if (!activeId || !doctorId) return;
    setLoading(true);
    api.get(`/opd/appointments?facility_id=${activeId}&apt_date=${dateStr}&doctor_id=${doctorId}`).then((r) => setAppts(r.data)).catch(() => setAppts([])).finally(() => setLoading(false));
  }, [activeId, doctorId, dateStr]);
  useEffect(load, [load]);

  const booked = Object.fromEntries(appts.filter((a) => a.status === "booked").map((a) => [a.start_time, a]));
  const doctorName = meta.doctors.find((d) => d.id === doctorId)?.name;

  const openBook = (s) => { setSlot(s); setPatient(null); setReason(""); setOpen(true); };
  const book = async () => {
    if (!patient) { toast.error("Select a patient"); return; }
    setSaving(true);
    try {
      await api.post("/opd/appointments", { patient_id: patient.id, doctor_id: doctorId, doctor_name: doctorName,
        date: dateStr, start_time: slot[0], end_time: slot[1], reason });
      toast.success("Appointment booked — patient notified");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const cancel = async (apt) => { try { await api.patch(`/opd/appointments/${apt.id}/status`, { status: "cancelled" }); toast.success("Appointment cancelled"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="appointments-page">
      <PageHeader title="Doctor Schedule" subtitle="Book patients into open slots on a doctor's day calendar." />

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="space-y-1"><Label className="text-xs">Doctor</Label>
          <select className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white min-w-[220px]" value={doctorId} onChange={(e) => setDoctorId(e.target.value)} data-testid="appt-doctor-select">
            {meta.doctors.length === 0 && <option value="">No doctors</option>}
            {meta.doctors.map((d) => <option key={d.id} value={d.id}>{d.name}{d.designation ? ` — ${d.designation}` : ""}</option>)}
          </select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" className="w-[180px]" value={dateStr} onChange={(e) => setDateStr(e.target.value)} data-testid="appt-date-input" /></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="slots-grid">
        {loading ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : !doctorId ? <div className="text-slate-400 text-center py-10">Select a doctor to view the schedule.</div> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {SLOTS.map(([s, e]) => {
              const b = booked[s];
              return b ? (
                <div key={s} className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5" data-testid={`slot-${s}`}>
                  <div className="flex items-center justify-between"><span className="font-mono text-sm font-semibold text-blue-800">{s}</span>{has("opd:edit") && <button onClick={() => cancel(b)} className="text-slate-400 hover:text-red-600" data-testid={`cancel-appt-${b.id}`}><X className="h-3.5 w-3.5" /></button>}</div>
                  <div className="text-xs text-slate-700 mt-1 flex items-center gap-1 truncate"><User className="h-3 w-3" />{b.patient_name}</div>
                </div>
              ) : (
                <button key={s} onClick={() => has("opd:create") && openBook([s, e])} disabled={!has("opd:create")} data-testid={`slot-${s}`}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2.5 text-left hover:border-blue-300 hover:bg-blue-50/40 transition-colors disabled:opacity-60">
                  <span className="font-mono text-sm font-medium text-slate-700">{s}</span>
                  <div className="text-xs text-emerald-600 mt-1">Available</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="book-appointment-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Book {dateStr} · {slot?.[0]}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs">Patient</Label><PatientPicker value={patient} onChange={setPatient} testId="appt-patient-picker" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Consultation, review" data-testid="appt-reason-input" /></div>
            <div className="text-xs text-slate-500">Doctor: <span className="font-medium">{doctorName}</span> · the patient receives an in-app notification.</div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={book} disabled={saving || !patient} className="bg-blue-700 hover:bg-blue-800" data-testid="confirm-booking-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Book & notify"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
